// ============================================================
// HARMONIC CHARTER — Music Theory Helpers
// ============================================================
// Pure functions. No side effects. No imports from other
// project modules. This is the lowest layer of the stack.
// ============================================================

import type { Degree, Accidental, Key, NoteRoot, Scale } from './types';

// ------------------------------------------------------------
// CHROMATIC SCALE
// ------------------------------------------------------------

/**
 * All 12 chromatic pitches as pitch class indices (0–11).
 * C = 0, C#/Db = 1, D = 2, ... B = 11
 */
export const PITCH_CLASS: Record<NoteRoot, number> = {
  'C':  0,  'C#': 1,  'Db': 1,
  'D':  2,  'D#': 3,  'Eb': 3,
  'E':  4,
  'F':  5,  'F#': 6,  'Gb': 6,
  'G':  7,  'G#': 8,  'Ab': 8,
  'A':  9,  'A#': 10, 'Bb': 10,
  'B':  11,
};

/**
 * Preferred sharp spellings for each pitch class.
 * Used when a key favours sharps.
 */
const SHARP_SPELLINGS: Record<number, NoteRoot> = {
  0: 'C', 1: 'C#', 2: 'D', 3: 'D#', 4: 'E',
  5: 'F', 6: 'F#', 7: 'G', 8: 'G#', 9: 'A',
  10: 'A#', 11: 'B',
};

/**
 * Preferred flat spellings for each pitch class.
 * Used when a key favours flats.
 */
const FLAT_SPELLINGS: Record<number, NoteRoot> = {
  0: 'C', 1: 'Db', 2: 'D', 3: 'Eb', 4: 'E',
  5: 'F', 6: 'Gb', 7: 'G', 8: 'Ab', 9: 'A',
  10: 'Bb', 11: 'B',
};

// ------------------------------------------------------------
// SCALE INTERVAL PATTERNS
// ------------------------------------------------------------

/**
 * Semitone intervals from root for each scale degree (1–7).
 * Index 0 is unused; degree 1 = index 1.
 */
const MAJOR_INTERVALS: Record<Degree, number> = {
  1: 0, 2: 2, 3: 4, 4: 5, 5: 7, 6: 9, 7: 11,
};

const MINOR_INTERVALS: Record<Degree, number> = {
  1: 0, 2: 2, 3: 3, 4: 5, 5: 7, 6: 8, 7: 10,
};

export function getScaleIntervals(scale: Scale): Record<Degree, number> {
  return scale === 'major' ? MAJOR_INTERVALS : MINOR_INTERVALS;
}

// ------------------------------------------------------------
// KEY PREFERENCE: SHARPS OR FLATS
// ------------------------------------------------------------

/**
 * Keys that conventionally use flat spellings.
 * All others use sharp spellings.
 */
const FLAT_KEYS = new Set<string>([
  'F major',
  'Bb major', 'Bb minor',
  'Eb major', 'Eb minor',
  'Ab major', 'Ab minor',
  'Db major', 'Db minor',
  'Gb major',
  'D minor', 'G minor', 'C minor',
]);

/**
 * Returns whether a key conventionally uses flat note spellings.
 */
export function keyUsesFlats(key: Key): boolean {
  return FLAT_KEYS.has(`${key.root} ${key.scale}`);
}

/**
 * Given a pitch class (0–11) and a key, returns the
 * preferred enharmonic spelling for that key.
 */
export function spellPitchClass(pitchClass: number, key: Key): NoteRoot {
  const pc = ((pitchClass % 12) + 12) % 12;
  return keyUsesFlats(key)
    ? FLAT_SPELLINGS[pc]
    : SHARP_SPELLINGS[pc];
}

// ------------------------------------------------------------
// DEGREE → NOTE RESOLUTION
// ------------------------------------------------------------

/**
 * Resolves a scale degree (with optional accidental) to a
 * concrete note name within a given key.
 *
 * Examples (key of C major):
 *   degree 1, null      → 'C'
 *   degree 4, null      → 'F'
 *   degree 7, 'flat'    → 'Bb'
 *   degree 4, 'sharp'   → 'F#'
 */
export function degreeToNote(
  degree: Degree,
  accidental: Accidental,
  key: Key,
): NoteRoot {
  const rootPc = PITCH_CLASS[key.root];
  const intervals = getScaleIntervals(key.scale);
  const degreePc = (rootPc + intervals[degree]) % 12;

  const accidentalOffset =
    accidental === 'flat' ? -1 :
    accidental === 'sharp' ? 1 : 0;

  const finalPc = ((degreePc + accidentalOffset) % 12 + 12) % 12;
  return spellPitchClass(finalPc, key);
}

// ------------------------------------------------------------
// NOTE → DEGREE RESOLUTION
// ------------------------------------------------------------

/**
 * Resolves a concrete note name to a scale degree and accidental
 * within a given key. Used by the chord-name parser.
 *
 * Returns null if the note cannot be resolved in this key
 * (should not happen for any valid chromatic note).
 *
 * Examples (key of C major):
 *   'F' → { degree: 4, accidental: null }
 *   'Bb' → { degree: 7, accidental: 'flat' }
 *   'F#' → { degree: 4, accidental: 'sharp' }
 */
export function noteToDegreee(
  note: NoteRoot,
  key: Key,
): { degree: Degree; accidental: Accidental } | null {
  const notePc = PITCH_CLASS[note];
  const rootPc = PITCH_CLASS[key.root];
  const intervals = getScaleIntervals(key.scale);

  // Try each degree to find a match (exact or with accidental)
  for (const d of [1, 2, 3, 4, 5, 6, 7] as Degree[]) {
    const degreePc = (rootPc + intervals[d]) % 12;

    if (degreePc === notePc) {
      return { degree: d, accidental: null };
    }
    if (((degreePc - 1 + 12) % 12) === notePc) {
      return { degree: d, accidental: 'flat' };
    }
    if (((degreePc + 1) % 12) === notePc) {
      return { degree: d, accidental: 'sharp' };
    }
  }

  return null;
}

// ------------------------------------------------------------
// TRANSPOSITION
// ------------------------------------------------------------

/**
 * Transposes a key by a given number of semitones.
 *
 * Examples:
 *   transpose({ root: 'C', scale: 'major' }, 2) → { root: 'D', scale: 'major' }
 *   transpose({ root: 'G', scale: 'major' }, -2) → { root: 'F', scale: 'major' }
 */
export function transposeKey(key: Key, semitones: number): Key {
  const rootPc = PITCH_CLASS[key.root];
  const newPc = ((rootPc + semitones) % 12 + 12) % 12;
  const newKey: Key = { root: 'C', scale: key.scale }; // placeholder root
  // Spell the new root using the new key's preference
  const newRoot = spellPitchClass(newPc, { root: SHARP_SPELLINGS[newPc], scale: key.scale });
  return { root: newRoot, scale: key.scale };
}

// ------------------------------------------------------------
// TIME SIGNATURE UTILITIES
// ------------------------------------------------------------

/**
 * Returns the total number of beats in a bar for a given time signature.
 */
export function beatsPerBar(numerator: number): number {
  return numerator;
}

/**
 * Infers chord positions from chord count and time signature.
 * Returns an array of beat positions (zero-indexed from bar start).
 *
 * Rules:
 *   1 chord  → [0] (whole bar)
 *   2 chords → [0, half]
 *   4 chords → [0, 1, 2, 3] in 4/4
 *   3 chords → [0, 2, 3] in 4/4 (2+1+1), [0, 1, 2] in 3/4
 *   other    → evenly spaced
 */
export function inferPositions(
  chordCount: number,
  timeNumerator: number,
): number[] {
  if (chordCount === 0) return [];
  if (chordCount === 1) return [0];

  // Special case: 3 chords in 4/4 → beats 0, 2, 3
  if (chordCount === 3 && timeNumerator === 4) {
    return [0, 2, 3];
  }

  const step = timeNumerator / chordCount;
  return Array.from({ length: chordCount }, (_, i) => i * step);
}

/**
 * Given an array of chord positions and a bar's time signature,
 * returns the duration of each chord in beats.
 * The last chord lasts until the end of the bar.
 */
export function positionsToDurations(
  positions: number[],
  timeNumerator: number,
): number[] {
  if (positions.length === 0) return [];
  return positions.map((pos, i) => {
    const next = positions[i + 1] ?? timeNumerator;
    return next - pos;
  });
}

/**
 * Legacy alias — kept for any code that still calls inferDurations.
 * @deprecated Use inferPositions instead.
 */
export function inferDurations(
  chordCount: number,
  timeNumerator: number,
): number[] {
  const positions = inferPositions(chordCount, timeNumerator);
  return positionsToDurations(positions, timeNumerator);
}

// ------------------------------------------------------------
// VALIDATION HELPERS
// ------------------------------------------------------------

/**
 * Returns true if a value is a valid scale degree.
 */
export function isValidDegree(n: number): n is Degree {
  return n >= 1 && n <= 7 && Number.isInteger(n);
}

/**
 * Returns true if a string is a recognised note root.
 */
export function isValidNoteRoot(s: string): s is NoteRoot {
  return s in PITCH_CLASS;
}

/**
 * Returns the semitone distance between two keys (for transposition UI).
 * Always returns a value 0–11.
 */
export function semitoneBetweenKeys(from: Key, to: Key): number {
  const fromPc = PITCH_CLASS[from.root];
  const toPc = PITCH_CLASS[to.root];
  return ((toPc - fromPc) % 12 + 12) % 12;
}